import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchPermits,
  exportPermitsCsv,
} from '../api/govopsPermitService';
import { downloadCsv } from '../api/govopsOperationsService';
import { formatGovOpsError, isGovOpsAuthError } from '../api/govopsErrors';
import { formatStatus, formatTimestamp } from '../constants/workflowLabels';
import { getPermitMapPosition } from '../utils/mapCoordinates';
import { printPermitNotice } from '../utils/pdfExport';
import PageShell from './PageShell';

function formatAddress(permit) {
  const loc = permit.alarmLocation || {};
  const parts = [
    loc.street || loc.address,
    loc.city,
    loc.state,
    loc.zipCode,
  ].filter(Boolean);
  if (parts.length) return parts.join(', ');
  const pos = getPermitMapPosition(permit);
  if (pos) return `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}`;
  return '—';
}

function shortId(id) {
  if (!id) return '—';
  const s = String(id);
  return s.length > 12 ? `${s.slice(0, 8)}…` : s;
}

const STATUS_CLASS = {
  intake: 'rhs-badge--intake',
  under_review: 'rhs-badge--review',
  notice_sent: 'rhs-badge--notice',
  violation_open: 'rhs-badge--violation',
  payment_pending: 'rhs-badge--payment',
  resolved: 'rhs-badge--resolved',
  closed: 'rhs-badge--closed',
  appealed: 'rhs-badge--appeal',
};

const PermitHistory = () => {
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [blockedMsg, setBlockedMsg] = useState('');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const loadPermits = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setBlockedMsg('');
    try {
      const permitList = await fetchPermits();
      setPermits(permitList);
    } catch (error) {
      if (!isGovOpsAuthError(error)) console.error(error);
      setBlockedMsg(formatGovOpsError(error));
      setPermits([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadPermits(false);
  }, [loadPermits]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return permits;
    return permits.filter((p) => {
      const hay = [
        p.id,
        p.type,
        p.description,
        p.status,
        p.permitNumber,
        formatAddress(p),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [permits, search]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const pageSafe = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(
    pageSafe * rowsPerPage,
    pageSafe * rowsPerPage + rowsPerPage
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const csv = await exportPermitsCsv();
      downloadCsv(
        `robinhoodspace-permit-history-${new Date().toISOString().slice(0, 10)}.csv`,
        csv
      );
    } catch (error) {
      if (error?.status === 401 || error?.status === 403 || error?.name?.includes('GovOps')) {
        setBlockedMsg(formatGovOpsError(error));
        return;
      }
      const Papa = (await import('papaparse')).default;
      const data = permits.map((permit) => ({
        'Permit ID': permit.id || '',
        'Permit Type': permit.type || '',
        Status: formatStatus(permit.status),
        Description: permit.description || '',
        Address: formatAddress(permit),
        Submitted: formatTimestamp(permit.createdAt || permit.date),
      }));
      downloadCsv(
        `robinhoodspace-permit-history-${new Date().toISOString().slice(0, 10)}.csv`,
        Papa.unparse(data)
      );
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <PageShell
        title="Permit history"
        subtitle="Review permits submitted through RobinhoodSpace."
      >
        <div className="rhs-loading" role="status" aria-live="polite">
          <span className="rhs-spinner" aria-hidden="true" />
          Loading permit history…
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Permit history"
      subtitle="Review permits submitted through RobinhoodSpace. Data is stored in GovOps for your organization."
      className="rhs-permit-history"
    >
      <div className="rhs-toolbar">
        <label className="rhs-search" htmlFor="permit-history-search">
          <span className="rhs-sr-only">Search permits</span>
          <input
            id="permit-history-search"
            type="search"
            placeholder="Search by type, ID, status, or address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={Boolean(blockedMsg)}
          />
        </label>
        <div className="rhs-toolbar-actions">
          <button
            type="button"
            className="rhs-btn rhs-btn--secondary"
            onClick={() => loadPermits(true)}
            disabled={refreshing || Boolean(blockedMsg)}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="rhs-btn"
            onClick={handleExport}
            disabled={exporting || Boolean(blockedMsg) || permits.length === 0}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <Link to="/permit" className="rhs-btn rhs-btn--outline">
            Submit permit
          </Link>
        </div>
      </div>

      {blockedMsg && (
        <div className="rhs-error-panel" role="alert">
          <strong>Unable to load permits</strong>
          <p className="rhs-muted">{blockedMsg}</p>
        </div>
      )}

      {!blockedMsg && (
        <p className="rhs-summary" role="status">
          Showing {filtered.length} of {permits.length} permit
          {permits.length === 1 ? '' : 's'}
          {search.trim() ? ` matching “${search.trim()}”` : ''}
        </p>
      )}

      {!blockedMsg && filtered.length === 0 && (
        <div className="rhs-empty-state">
          <h2>No permits yet</h2>
          <p className="rhs-muted">
            {search.trim()
              ? 'No permits match your search. Try a different term or clear the filter.'
              : 'When you submit a false alarm permit, it will appear here with status and location.'}
          </p>
          {!search.trim() && (
            <Link to="/permit" className="rhs-btn">
              Submit your first permit
            </Link>
          )}
        </div>
      )}

      {!blockedMsg && pageRows.length > 0 && (
        <>
          <div className="rhs-table-wrap">
            <table className="rhs-table rhs-table--history">
              <thead>
                <tr>
                  <th scope="col">Permit</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th scope="col">Location</th>
                  <th scope="col">Submitted</th>
                  <th scope="col">
                    <span className="rhs-sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((permit) => {
                  const statusKey = permit.status || 'intake';
                  const badgeClass =
                    STATUS_CLASS[statusKey] || 'rhs-badge--default';
                  return (
                    <tr key={permit.id}>
                      <td data-label="Permit">
                        <span className="rhs-permit-id" title={permit.id}>
                          {shortId(permit.id)}
                        </span>
                        {permit.permitNumber && (
                          <span className="rhs-muted rhs-permit-meta">
                            #{permit.permitNumber}
                          </span>
                        )}
                      </td>
                      <td data-label="Type">{permit.type || '—'}</td>
                      <td data-label="Status">
                        <span className={`rhs-badge ${badgeClass}`}>
                          {formatStatus(statusKey)}
                        </span>
                      </td>
                      <td data-label="Location" className="rhs-cell-location">
                        {formatAddress(permit)}
                      </td>
                      <td data-label="Submitted">
                        {formatTimestamp(permit.createdAt || permit.date)}
                      </td>
                      <td data-label="Actions" className="rhs-cell-actions">
                        <Link
                          to={`/permit-details/${permit.id}`}
                          className="rhs-table-link"
                        >
                          Details
                        </Link>
                        {' · '}
                        <Link
                          to="/map"
                          state={{ permitId: permit.id }}
                          className="rhs-table-link"
                        >
                          Map
                        </Link>
                        {' · '}
                        <button
                          type="button"
                          className="rhs-link-btn"
                          onClick={() => printPermitNotice(permit)}
                        >
                          PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filtered.length > rowsPerPage && (
            <nav
              className="rhs-pagination"
              aria-label="Permit history pages"
            >
              <button
                type="button"
                className="rhs-btn rhs-btn--secondary"
                disabled={pageSafe <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </button>
              <span className="rhs-muted">
                Page {pageSafe + 1} of {pageCount}
              </span>
              <button
                type="button"
                className="rhs-btn rhs-btn--secondary"
                disabled={pageSafe >= pageCount - 1}
                onClick={() =>
                  setPage((p) => Math.min(pageCount - 1, p + 1))
                }
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </PageShell>
  );
};

export default PermitHistory;
